import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, RefreshCw, Server, Activity, Box, Play, Square, RotateCcw, FolderSearch, X, FileText, ExternalLink, Cpu, MemoryStick, HardDrive, Clock, Wifi, WifiOff, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useServer, useDeleteServer, useTestConnection, useServerStats, useServerContainers, useContainerAction } from '../../hooks/useServers';
import { serversApi } from '../../lib/api/servers';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { FileExplorerModal } from '../../components/server/FileExplorerModal';
import { FileEditorModal } from '../../components/server/FileEditorModal';
import { DiscoveryButton } from '../../components/server/DiscoveryButton';
import { useQueryClient } from '@tanstack/react-query';
import { useToastStore } from '../../stores/toastStore';
import { getErrorMessage } from '../../lib/api/client';

export function ServerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const { data: server, isLoading, error } = useServer(id!);
  const { data: stats, refetch: refetchStats } = useServerStats(id!);
  const { data: containersData, refetch: refetchContainers, isLoading: containersLoading } = useServerContainers(id!);
  const deleteServer = useDeleteServer();
  const testConnection = useTestConnection();
  const containerAction = useContainerAction();
  const [connectionResult, setConnectionResult] = useState<{ success: boolean; message: string } | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // File explorer modal state
  const [showFileExplorer, setShowFileExplorer] = useState(false);
  const [settingPath, setSettingPath] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // File editor modal state
  const [showFileEditor, setShowFileEditor] = useState(false);
  const [editFilePath, setEditFilePath] = useState<string>('');

  // Auto-refresh stats every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refetchStats();
      refetchContainers();
      setLastUpdated(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, refetchStats, refetchContainers]);

  // Time ago helper
  const getTimeAgo = () => {
    const seconds = Math.floor((new Date().getTime() - lastUpdated.getTime()) / 1000);
    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const [timeAgo, setTimeAgo] = useState(getTimeAgo());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeAgo(getTimeAgo());
    }, 1000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const handleDelete = async () => {
    await deleteServer.mutateAsync(id!);
    navigate('/servers');
    setShowDeleteConfirm(false);
  };

  const handleTestConnection = async () => {
    setConnectionResult(null);
    try {
      const result = await testConnection.mutateAsync(id!);
      setConnectionResult(result);
    } catch (error: unknown) {
      setConnectionResult({
        success: false,
        message: getErrorMessage(error),
      });
    }
  };

  const handleContainerAction = async (containerId: string, action: 'start' | 'stop' | 'restart') => {
    try {
      await containerAction.mutateAsync({ serverId: id!, containerId, action });
      addToast({ type: 'success', message: `Container ${action} successful` });
    } catch (error: unknown) {
      addToast({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleSelectFile = async (filePath: string) => {
    setSettingPath(true);
    try {
      await serversApi.setDockerComposePath(id!, filePath);
      queryClient.invalidateQueries({ queryKey: ['server', id] });
      queryClient.invalidateQueries({ queryKey: ['server-containers', id] });
      addToast({ type: 'success', message: 'Docker compose path updated' });
    } catch (error: unknown) {
      addToast({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setSettingPath(false);
    }
  };

  const handleClearDockerComposePath = async () => {
    setSettingPath(true);
    try {
      await serversApi.setDockerComposePath(id!, null);
      queryClient.invalidateQueries({ queryKey: ['server', id] });
      queryClient.invalidateQueries({ queryKey: ['server-containers', id] });
      addToast({ type: 'success', message: 'Docker compose path cleared' });
    } catch (error: unknown) {
      addToast({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setSettingPath(false);
    }
  };

  const handleOpenFileEditor = (filePath: string) => {
    setEditFilePath(filePath);
    setShowFileEditor(true);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !server) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-red-600">Server not found</p>
        <Button variant="link" onClick={() => navigate('/servers')}>
          Back to Servers
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/servers')}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Servers
        </Button>

        <div className="flex gap-2">
          <Link to={`/servers/${id}/edit`}>
            <Button variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleteServer.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Server Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-100">
              <Server className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <CardTitle>{server.name}</CardTitle>
              <p className="text-sm text-gray-500">
                {server.username}@{server.host}:{server.port}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Host</dt>
              <dd className="mt-1 text-gray-900">{server.host}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Port</dt>
              <dd className="mt-1 text-gray-900">{server.port}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Username</dt>
              <dd className="mt-1 text-gray-900">{server.username}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Type</dt>
              <dd className="mt-1 text-gray-900">
                {server.is_local ? 'Local' : 'Remote'}
              </dd>
            </div>
            {server.description && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-gray-900">{server.description}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-gray-900">
                {new Date(server.created_at).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Updated</dt>
              <dd className="mt-1 text-gray-900">
                {new Date(server.updated_at).toLocaleString()}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Server Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Server Statistics
              <span className="text-xs text-gray-500 font-normal ml-2">
                Updated {timeAgo}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="gap-2"
              >
                <div className={`h-2 w-2 rounded-full ${autoRefresh ? 'bg-green-500' : 'bg-gray-400'}`} />
                Auto: {autoRefresh ? 'ON' : 'OFF'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                refetchStats();
                refetchContainers();
                setLastUpdated(new Date());
              }}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats ? (
            <div className="space-y-6">
              {/* Resource Meters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* CPU */}
                {stats.load_average && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 bg-blue-500 rounded-lg">
                        <Cpu className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-700 font-medium">CPU Usage</p>
                        <p className="text-2xl font-bold text-blue-900">{stats.load_average}</p>
                      </div>
                    </div>
                    <div className="bg-blue-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          parseFloat(stats.load_average) >= 85 ? 'bg-red-500' :
                          parseFloat(stats.load_average) >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.min(parseFloat(stats.load_average) || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Memory */}
                {stats.memory_usage && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 bg-purple-500 rounded-lg">
                        <MemoryStick className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-purple-700 font-medium">Memory</p>
                        <p className="text-2xl font-bold text-purple-900">{stats.memory_usage}</p>
                      </div>
                    </div>
                    <div className="bg-purple-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(parseFloat(stats.memory_usage) || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Disk */}
                {stats.disk_usage && (
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <HardDrive className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-green-700 font-medium">Disk Usage</p>
                        <p className="text-2xl font-bold text-green-900">{stats.disk_usage}</p>
                      </div>
                    </div>
                    <div className="bg-green-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          parseFloat(stats.disk_usage) >= 85 ? 'bg-red-500' :
                          parseFloat(stats.disk_usage) >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(parseFloat(stats.disk_usage) || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Uptime */}
              {stats.uptime && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Clock className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="text-xs text-gray-500">Server Uptime</p>
                    <p className="text-sm font-semibold text-gray-900">{stats.uptime}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-gray-300 mx-auto mb-3 animate-pulse" />
              <p className="text-gray-500">Loading server statistics...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Docker Containers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Box className="h-5 w-5" />
              Docker Containers
              {containersData && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                  {containersData.count}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowFileExplorer(true)}>
                <FolderSearch className="h-4 w-4 mr-2" />
                {server.docker_compose_path ? 'Change' : 'Select'} Compose File
              </Button>
              <Button size="sm" variant="outline" onClick={() => refetchContainers()}>
                <RefreshCw className={`h-4 w-4 mr-2 ${containersLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Docker Compose Path - Always visible */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              {server.docker_compose_path ? (
                <button
                  onClick={() => handleOpenFileEditor(server.docker_compose_path!)}
                  className="text-sm text-primary-600 hover:text-primary-800 hover:underline flex items-center gap-1"
                >
                  {server.docker_compose_path}
                  <ExternalLink className="h-3 w-3" />
                </button>
              ) : (
                <span className="text-sm text-gray-400 italic">No compose file selected</span>
              )}
            </div>
            {server.docker_compose_path && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearDockerComposePath}
                disabled={settingPath}
                className="text-gray-500 hover:text-red-600"
                title="Clear compose file"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {containersData?.error ? (
            <div className="p-4 bg-red-50 text-red-800 rounded-md">
              <p className="font-medium">Failed to fetch containers</p>
              <p className="text-sm mt-1">{containersData.error}</p>
            </div>
          ) : containersData?.containers && containersData.containers.length > 0 ? (
            <div className="space-y-3">
              {containersData.containers.map((container) => {
                const isRunning = container.status.toLowerCase().includes('up') || container.status.toLowerCase().includes('running');
                return (
                  <div
                    key={container.id}
                    className={`p-4 border rounded-lg transition-all hover:shadow-md ${
                      isRunning
                        ? 'border-green-200 bg-gradient-to-r from-green-50 to-transparent'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`h-2.5 w-2.5 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                          <span className="font-semibold text-gray-900">{container.name}</span>
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              isRunning
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {container.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                          <Box className="h-3 w-3 inline mr-1" />
                          <span className="font-mono text-xs">{container.image}</span>
                        </p>
                        {container.ports && container.ports !== 'None' && (
                          <p className="text-xs text-gray-500">
                            🔌 Ports: {container.ports}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {isRunning ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleContainerAction(container.name, 'restart')}
                              disabled={containerAction.isPending}
                              title="Restart Container"
                              className="hover:bg-yellow-50 hover:border-yellow-300"
                            >
                              <RotateCcw className="h-4 w-4 text-yellow-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleContainerAction(container.name, 'stop')}
                              disabled={containerAction.isPending}
                              title="Stop Container"
                              className="hover:bg-red-50 hover:border-red-300"
                            >
                              <Square className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleContainerAction(container.name, 'start')}
                            disabled={containerAction.isPending}
                            title="Start Container"
                            className="hover:bg-green-50 hover:border-green-300"
                          >
                            <Play className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : containersLoading ? (
            <div className="text-center py-8">
              <Box className="h-12 w-12 text-gray-300 mx-auto mb-3 animate-pulse" />
              <p className="text-gray-500">Loading containers...</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <Box className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-2">No Docker containers running</p>
              <p className="text-xs text-gray-400">Select a docker-compose file to manage containers</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connection Test */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wifi className="h-5 w-5" />
              Connection Test
            </div>
            <Button
              size="sm"
              onClick={handleTestConnection}
              disabled={testConnection.isPending}
              variant={connectionResult?.success ? 'outline' : 'default'}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${testConnection.isPending ? 'animate-spin' : ''}`} />
              {testConnection.isPending ? 'Testing...' : 'Test Connection'}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {testConnection.isPending ? (
            <div className="p-6 text-center">
              <RefreshCw className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
              <p className="text-gray-700 font-medium">Testing SSH connection...</p>
              <p className="text-sm text-gray-500 mt-1">This may take a few seconds</p>
            </div>
          ) : connectionResult ? (
            <div
              className={`p-4 rounded-lg border-2 ${
                connectionResult.success
                  ? 'border-green-300 bg-gradient-to-br from-green-50 to-green-100'
                  : 'border-red-300 bg-gradient-to-br from-red-50 to-red-100'
              }`}
            >
              <div className="flex items-start gap-3">
                {connectionResult.success ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={`font-semibold text-lg ${connectionResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {connectionResult.success ? '✅ Connection Successful!' : '❌ Connection Failed'}
                  </p>
                  <p className={`text-sm mt-2 ${connectionResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {connectionResult.message}
                  </p>
                  {connectionResult.success && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-green-700 font-medium mb-2">✓ SSH connection verified</p>
                      <p className="text-xs text-green-600">Server is reachable and credentials are valid</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 px-4">
              <div className="bg-gray-100 rounded-full h-16 w-16 flex items-center justify-center mx-auto mb-4">
                <Wifi className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-700 font-medium mb-1">Test SSH Connection</p>
              <p className="text-sm text-gray-500">
                Click "Test Connection" to verify server accessibility
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto-Discovery */}
      <Card>
        <CardHeader>
          <CardTitle>Auto-Discovery</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Automatically discover all resources on this server:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-2">
              <li>Docker container apps from docker-compose files</li>
              <li>Static web applications</li>
              <li>Nginx domains and SSL certificates</li>
              <li>Environment variables and secrets</li>
            </ul>
            <div className="pt-2">
              <DiscoveryButton
                serverId={id!}
                onComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ['server', id] });
                  queryClient.invalidateQueries({ queryKey: ['server-containers', id] });
                  addToast({
                    type: 'success',
                    message: 'Discovery completed! Resources have been updated.'
                  });
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Server Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Server"
        message={
          <>
            Are you sure you want to delete <strong>{server.name}</strong>?
            This will remove all associated apps and configurations. This action cannot be undone.
          </>
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleteServer.isPending}
      />

      {/* File Explorer Modal */}
      <FileExplorerModal
        isOpen={showFileExplorer}
        onClose={() => setShowFileExplorer(false)}
        serverId={id!}
        onSelect={handleSelectFile}
        title="Select Docker Compose File"
      />

      {/* File Editor Modal */}
      <FileEditorModal
        isOpen={showFileEditor}
        onClose={() => setShowFileEditor(false)}
        serverId={id!}
        filePath={editFilePath}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: ['server-containers', id] });
        }}
      />
    </div>
  );
}
